import { FC, useState, useEffect, useRef } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { showSwal } from '../../../../ui-kit'
import { Card, Row, Col, Form, Button, Spinner, InputGroup } from 'react-bootstrap'
import { getAgreementById, submitChapterToReview, getChapterById, uploadChapterPdf } from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import { useNavigate, useParams } from 'react-router-dom'
import { AgreementChapter } from './core/_models'
import './TradeDataAgreementChapterPage.scss'

import { useAbility } from '@casl/react'
import { AbilityContext } from '../../../modules/auth/core/AbilityContext'
import SummernoteEditor from '../../../../ui-kit/SummerNote/SummerNote'



const FIELDS_TO_COMPARE = [
  'chapter_number',
  'chapter_name',
  'article_number',
  'article_name',
  'article_text',
  'article_text_display',
  'chapter_id',
  'article_id',
]

function isChapterChanged(original: any, current: any) {
  for (const key of FIELDS_TO_COMPARE) {
    const oldVal = (original?.[key] ?? '').trim()
    const newVal = (current?.[key] ?? '').trim()
    if (oldVal !== newVal) return true // found difference
  }
  return false
}

const TradeDataAgreementChapterPage: FC<{ subject: string }> = ({ subject }) => {
  const ability = useAbility(AbilityContext)
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const { idAgreement } = useParams()
  const { idChapter } = useParams()
  const isEditMode = Boolean(idChapter)

  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isUploadingPdf, setIsUploadingPdf] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isCodeMode, setIsCodeMode] = useState(false)
  const [codeValue, setCodeValue] = useState('')
  const [wordCount, setWordCount] = useState(0)
  const [agreementChapter, setAgreementChapter] = useState<AgreementChapter>({
    id: '',
    chapter_number: '',
    chapter_name: '',
    article_number: '',
    article_name: '',
    article_text: '',
    article_text_display: '',
    chapter_id: '',
    article_id: '',
    nwords_new: null,
    rta_id: null,
    agreement: '',
    xml_file: '',
    source: '',
  })

  const [originalChapter, setOriginalChapter] = useState<AgreementChapter | null>(null)

  const placeholders: Record<string, string> = {
    chapter_id: 'Enter chapter ID',
    chapter_name: 'Enter chapter name',
    chapter_number: 'Enter chapter number',
    article_id: 'Enter article ID',
    article_name: 'Enter article name',
    article_number: 'Enter article number',
    article_text: 'Enter article text',
    article_text_display: 'Enter article text to display...',
    source: 'Enter link source',
  }

  const getSuffixAfterUnderscore = (value?: string) => {
    if (!value) return ''
    const parts = value.split('_')
    return parts.length > 1 ? parts[parts.length - 1] : value
  }

  const fetchOptionAgreement = async () => {
    try {
      setIsLoadingAgreement(true)

      let detailRes

      if (isEditMode && idChapter) {
        detailRes = await getChapterById(axiosPrivate, { idChapter })
      } else if (idAgreement) {
        detailRes = await getAgreementById(axiosPrivate, { idAgreement })
      }

      if (detailRes?.data?.data) {
        const resData = detailRes.data.data
        const transformedData = isEditMode
          ? {
            ...resData,
            chapter_id: getSuffixAfterUnderscore(resData.chapter_id),
            article_id: getSuffixAfterUnderscore(resData.article_id),
          }
          : resData

        setAgreementChapter((prev) => ({
          ...prev,
          ...transformedData,
        }))
        setOriginalChapter(resData)

        const articleTextDisplay = resData.article_text_display ?? ''
        setWordCount(countWordsFromHtml(articleTextDisplay))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAgreement(false)
    }
  }

  useEffect(() => {
    fetchOptionAgreement()
  }, [])

  // Add or Edit
  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setAgreementChapter((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = ['article_name', 'article_id']

    const isValid = requiredFields.every((key) => {
      const val = (agreementChapter as any)[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  const convertFromHTML = (html: string): string => {
    const text = html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    return text
  }

  const countWordsFromHtml = (html: string): number => {
    const text = convertFromHTML(html)
    if (!text) return 0
    return text.split(' ').length
  }

  useEffect(() => {
    validateRequiredFields()
  }, [agreementChapter])

  const handleSubmit = () => {
    const finalHtml = isCodeMode ? codeValue : agreementChapter.article_text_display || ''
    const finalWordCount = countWordsFromHtml(finalHtml)
    setWordCount(finalWordCount)

    if (ability.can('update', subject) && originalChapter) {
      const isChanged = isChapterChanged(originalChapter, agreementChapter)
      if (!isChanged) {
        showSwal({
          title: 'No changes detected',
          text: 'You haven’t made any modifications to the data.',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Go back to list',
          cancelButtonText: 'Keep editing',
        }).then((result) => {
          if (result.isConfirmed) {
            navigate(`/agreement/${idAgreement ?? agreementChapter.rta_id}`)
          }
        })
        return
      }
    }

    const formData = new FormData()
    formData.append('rta_id', String(idAgreement ?? agreementChapter.rta_id))
    if (idChapter) {
      formData.append('rta_chapter_id', String(idChapter))
    }
    formData.append('title', agreementChapter.agreement || '')
    if (agreementChapter.xml_file) {
      formData.append('filename', agreementChapter.xml_file)
    }
    formData.append('chapter_number', agreementChapter.chapter_number || '')
    formData.append('chapter_name', agreementChapter.chapter_name || '')
    formData.append('article_number', agreementChapter.article_number || '')
    formData.append('article_name', agreementChapter.article_name || '')
    formData.append('article_text', convertFromHTML(agreementChapter.article_text_display ?? ''))
    formData.append('article_text_display', agreementChapter.article_text_display || '')
    formData.append(
      'chapter_id',
      (isEditMode ? agreementChapter.rta_id : agreementChapter.id) +
      '_' +
      agreementChapter.chapter_id
    )
    formData.append(
      'article_id',
      (isEditMode ? agreementChapter.rta_id : agreementChapter.id) +
      '_' +
      agreementChapter.article_id
    )
    formData.append('nwords_new', String(finalWordCount))
    formData.append('source', agreementChapter.source || '')
    if (selectedFile) {
      formData.append('source_file', selectedFile)
    }

    showSwal({
      title: `Are you sure you want to ${isEditMode ? 'update' : 'create'} this agreement?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setIsLoadingButton(true)

      submitChapterToReview(axiosPrivate, formData)
        .then((res) => {
          if (res.status === 201) {
            showSwal(
              ability.can('create', subject)
                ? 'Data submitted'
                : 'Data submitted for review',
              undefined,
              'success'
            ).then(() => {
              navigate(`/agreement/${idAgreement ?? agreementChapter.rta_id}`)
            })
          }
        })
        .catch((err) => {
          console.error(err)
          showSwal('Error', 'Failed to create data', 'error')
        })
        .finally(() => {
          setIsLoadingButton(false)
        })
    })
  }

  const handleUploadPdf = () => {
    if (!selectedFile || !idChapter) return

    setIsUploadingPdf(true)
    const formData = new FormData()
    formData.append('source_file', selectedFile)

    uploadChapterPdf(axiosPrivate, idChapter, formData)
      .then((res) => {
        if (res.status === 200) {
          showSwal({
            title: 'Import Job Queued',
            text: 'The job is being processed in the queue. You can check the progress in the File Upload Queue menu.',
            icon: 'info',
            confirmButtonText: 'OK',
          }).then(() => {
            setSelectedFile(null)
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
            fetchOptionAgreement()
          })
        }
      })
      .catch((err) => {
        console.error(err)
        const errMsg = err?.response?.data?.message || 'Failed to queue the upload job'
        showSwal('Error', errMsg, 'error')
      })
      .finally(() => {
        setIsUploadingPdf(false)
      })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(`/agreement/${idAgreement ?? agreementChapter.rta_id}`)
      }
    })
  }
  // End of Add or Edit

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'agreement',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: agreementChapter.agreement ?? '-',
            path: `agreement/${idAgreement ?? agreementChapter.rta_id}`,
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isEditMode ? 'Edit' : 'Add'}
      </PageTitle>
      <div className='TradeDataAgreementChapterPage'>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isEditMode ? `Edit Chapter` : 'Add New Chapter'}
                      </h3>
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoadingAgreement ? (
                  <Form className='form-agreement'>
                    <div className='row'>
                      <div className='col-12'>
                        <h3>RTA's Information</h3>
                      </div>
                      <div className='col-md-12 mb-3'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          Name
                        </Form.Label>
                        <div className='data-value'>{agreementChapter.agreement}</div>
                        {agreementChapter.xml_file ? (
                          <a
                            href={agreementChapter.xml_file}
                            target='_blank'
                            rel='noreferrer'
                            className='text-link'
                          >
                            check XML File
                          </a>
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                    <hr />
                    <div className='row'>
                      <div className='col-12'>
                        <h3>Chapter Information</h3>
                      </div>
                      <div className='col-md-12'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          Chapter Name
                        </Form.Label>
                        <Form.Control
                          type='text'
                          name='chapter_name'
                          placeholder={placeholders['chapter_name']}
                          value={agreementChapter.chapter_name ?? ''}
                          onChange={handleValueChange}
                        />
                      </div>
                      <div className='col-md-4'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          Chapter Number
                        </Form.Label>
                        <Form.Control
                          type='text'
                          name='chapter_number'
                          placeholder={placeholders['chapter_number']}
                          value={agreementChapter.chapter_number ?? ''}
                          onChange={handleValueChange}
                        />
                      </div>
                      <div className='col-md-4'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          <span>Chapter Id</span>
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            {isEditMode ? agreementChapter.rta_id : agreementChapter.id}_
                          </InputGroup.Text>
                          <Form.Control
                            type='text'
                            name='chapter_id'
                            placeholder={placeholders['chapter_id']}
                            value={agreementChapter.chapter_id ?? ''}
                            onChange={handleValueChange}
                          />
                        </InputGroup>
                        <small className='text-muted'>
                          <i>*please input the ordering number only</i>
                        </small>
                      </div>
                    </div>
                    <hr />
                    <div className='row'>
                      <div className='col-12'>
                        <h3>Article</h3>
                      </div>
                      <div className='col-md-12 mb-3'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          <span className='required'>Article Name</span>
                        </Form.Label>
                        <Form.Control
                          type='text'
                          name='article_name'
                          placeholder={placeholders['article_name']}
                          value={agreementChapter.article_name ?? ''}
                          onChange={handleValueChange}
                        />
                      </div>
                      <div className='col-md-4 mb-3'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          Article Number
                        </Form.Label>
                        <Form.Control
                          type='text'
                          name='article_number'
                          placeholder={placeholders['article_number']}
                          value={agreementChapter.article_number ?? ''}
                          onChange={handleValueChange}
                        />
                      </div>
                      <div className='col-md-4 mb-3'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          <span className='required'>Article Id</span>
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            {isEditMode ? agreementChapter.rta_id : agreementChapter.id}_
                          </InputGroup.Text>
                          <Form.Control
                            type='text'
                            name='article_id'
                            placeholder={placeholders['article_id']}
                            value={agreementChapter.article_id ?? ''}
                            onChange={handleValueChange}
                          />
                        </InputGroup>
                        <small className='text-muted'>
                          <i>*please input the ordering number only</i>
                        </small>
                      </div>
                      <div className='col-md-12 mt-3'>
                        <div className='d-flex justify-content-between align-items-start mb-2'>
                          <div>
                            <Form.Label className='text-primary m-0'>
                              Article Text Display
                            </Form.Label>
                          </div>
                          <div className='text-muted small'>{wordCount} words</div>
                        </div>
                        <SummernoteEditor
                          value={agreementChapter.article_text_display || ''}
                          onChange={(html) => {
                            setAgreementChapter((prev) => ({
                              ...prev,
                              article_text_display: html,
                            }))
                            setWordCount(countWordsFromHtml(html))
                          }}
                        />
                      </div>
                    </div>
                    <hr />
                    <div className='row'>
                      <div className='col-12'>
                        <h3>Source</h3>
                      </div>
                      <div className='col-md-12 mb-3'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          Link
                        </Form.Label>
                        <Form.Control
                          type='text'
                          name='source'
                          placeholder={placeholders['source']}
                          value={agreementChapter.source ?? ''}
                          onChange={handleValueChange}
                        />
                      </div>
                      <div className='col-md-12 mb-3'>
                        <Form.Label className='text-primary me-0 ps-0' column>
                          Source File (PDF only)
                        </Form.Label>
                        <div className='d-flex align-items-center gap-2 mt-1'>
                          <Form.Control
                            type='file'
                            accept='.pdf'
                            ref={fileInputRef}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const files = e.target.files
                              if (files && files.length > 0) {
                                const file = files[0]
                                if (file.type !== 'application/pdf') {
                                  showSwal('Error', 'Only PDF files are allowed', 'error')
                                  e.target.value = ''
                                  setSelectedFile(null)
                                  return
                                }
                                setSelectedFile(file)
                              } else {
                                setSelectedFile(null)
                              }
                            }}
                          />
                          {isEditMode && selectedFile && (
                            <Button
                              variant='secondary'
                              onClick={handleUploadPdf}
                              disabled={isUploadingPdf}
                            >
                              {isUploadingPdf ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-1' />
                                  Uploading
                                </>
                              ) : (
                                'Upload'
                              )}
                            </Button>
                          )}
                        </div>
                        {agreementChapter.source_file_path && (
                          <a
                            href={agreementChapter.source_file_path}
                            download
                            target='_blank'
                            rel='noreferrer'
                            className='d-block text-link mt-2'
                          >
                            Download Source File
                          </a>
                        )}
                      </div>
                    </div>
                    <div className='row pt-5'>
                      <div className='col-lg-12 d-flex justify-content-end gap-3'>
                        <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button
                          variant='primary'
                          onClick={handleSubmit}
                          disabled={isLoadingButton || !isFormValid}
                        >
                          {isLoadingButton ? (
                            <>
                              <Spinner animation='border' size='sm' className='me-2' />
                              Submit
                            </>
                          ) : (
                            'Submit'
                          )}
                        </Button>
                      </div>
                    </div>
                  </Form>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{ height: '50vh' }}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default TradeDataAgreementChapterPage
